<template>
  <div class="layout-fullscreen layout-fullscreen--no-header">
    <HeaderBar :modifiers="['main']">
      <div class="header-bar__title typo-title">
        {{ $t('regions.manage_regions') }}
      </div>

      <template #right>
        <router-link
          class="button"
          :to="{ name: 'configurationPlants' }"
        >
          {{ $t('general.cancel') }}
        </router-link>
        <FormSubmitButton
          :buttonLabel="$t('regions.save_region')"
          class="show-for-medium"
          :isSaving="saving"
          @click="save"
        />
      </template>
    </HeaderBar>

    <AlertBar v-if="errorMessage">
      {{ errorMessage }}
    </AlertBar>

    <div class="layout-panel hide-for-medium">
      <div class="layout-panel__panel">
        <EmptyMessage
          :message="$t('regions.unsupported_device_size_message')"
          :title="$t('regions.unsupported_device_size')"
        >
          <ExclamationOctagonSvg />

          <template #actions>
            <router-link
              class="button button--inverse"
              :to="{ name: 'regions', params: { issueType: 'plant' } }"
            >
              {{ $t('general.go_back') }}
            </router-link>
          </template>
        </EmptyMessage>
      </div>
    </div>

    <div class="layout-regions show-for-medium">
      <div class="layout-regions__regions">
        <RegionTree
          v-if="regionTree && !loading"
          :allRegions="false"
          class="layout-regions__regions-tree"
          :modifiers="['manage-scroll']"
          :regionTree="regionTree"
          :selectedRegion="selectedRegion"
          @select="selectRegion"
        />
        <Loader v-else />
      </div>

      <div class="layout-regions__content">
        <div class="layout-regions__issues">
          <EmptyMessage
            v-if="!selectedRegion"
            :title="$t('regions.no_selection')"
          />
          <template v-if="selectedRegion">
            <div class="form form--default scroll-container">
              <FormRow
                v-if="selectedRegion.errors && selectedRegion.errors.bounds"
                :errors="selectedRegion.errors && selectedRegion.errors.bounds"
                :modifiers="['narrow']"
              />

              <div class="form__group">
                <FormRow
                  :errors="selectedRegion.errors && selectedRegion.errors.name"
                  :label="$t('regions.region_name_label')"
                  labelFor="region_name"
                  :modifiers="['narrow']"
                >
                  <div class="form-row__input">
                    <div :class="{ 'input': true, 'input--error': selectedRegion.errors && selectedRegion.errors.name }">
                      <input
                        id="region_name"
                        v-model="selectedRegion.name"
                        :placeholder="$t('shafts.group.input_text')"
                        type="text"
                        @input="validateInput('name')"
                      >
                    </div>
                  </div>
                </FormRow>

                <FormRow
                  :label="$t('regions.region_type_label')"
                  :modifiers="['narrow', 'lateral']"
                >
                  <div class="form-row__input">
                    <Select
                      v-model="selectedRegion.region_type"
                      :items="regionTypeOptions"
                      :labelIcons="regionTypeLabelIcons"
                      labelClasses="select__label--flat"
                      showIconsInList
                      @update:modelValue="updateRegionType"
                    />
                  </div>
                </FormRow>

                <FormRow
                  v-if="!currentOperation?.policy?.sap_v1"
                  :errors="selectedRegion.errors && selectedRegion.errors.external_reference"
                  :label="$t('regions.external_reference')"
                  labelFor="external_reference"
                  :modifiers="['narrow']"
                >
                  <div class="form-row__input">
                    <div :class="{ 'input': true, 'input--error': selectedRegion.errors && selectedRegion.errors.external_reference }">
                      <input
                        id="external_reference"
                        v-model="selectedRegion.external_reference"
                        :placeholder="$t('shafts.group.input_text')"
                        type="text"
                      >
                    </div>
                  </div>
                </FormRow>

                <template v-if="currentOperation.policy?.sap_v1">
                  <p class="form__group-header">
                    {{ $t('regions.sap.sap_details') }}
                  </p>
                  <div class="form__group-inner form__group-inner--narrow">
                    <template v-if="!selectedRegion.isNew">
                      <div class="form__group-content form__group-content--narrow">
                        <p>{{ $t('regions.sap.asset_name') }}</p>
                        <p>{{ selectedRegion?.kumba_sap_information?.data?.asset_name }}</p>
                      </div>
                      <div class="form__group-content form__group-content--narrow">
                        <p>{{ $t('regions.sap.work_centre') }}</p>
                        <p>{{ selectedRegion?.kumba_sap_information?.data?.work_centre }}</p>
                      </div>
                      <div class="form__group-content form__group-content--narrow">
                        <p>{{ $t('regions.sap.planner_group') }}</p>
                        <p>{{ selectedRegion?.kumba_sap_information?.data?.planner_group }}</p>
                      </div>
                    </template>

                    <FormRow
                      :errors="selectedRegion.errors && selectedRegion.errors['kumba_sap_information.functional_location']"
                      :label="$t('regions.sap.functional_location')"
                      labelFor="region_functional_location"
                      :modifiers="['narrow']"
                    >
                      <div class="form-row__input">
                        <div
                          :class="{
                            'input': true,
                            'input--error': selectedRegion.errors && selectedRegion.errors['kumba_sap_information.functional_location']
                          }"
                        >
                          <input
                            id="region_functional_location"
                            v-model="selectedRegion.kumba_sap_information.data.functional_location"
                            :placeholder="$t('shafts.group.input_text')"
                            type="text"
                            @input="validateInput('kumba_sap_information.functional_location')"
                          >
                        </div>
                      </div>
                    </FormRow>

                    <FormRow
                      :errors="selectedRegion.errors && selectedRegion.errors['kumba_sap_information.destination_site']"
                      :label="$t('regions.sap.destination_site')"
                      labelFor="region_destination_site"
                      :modifiers="['narrow']"
                    >
                      <div class="form-row__input">
                        <div
                          :class="{
                            'input': true,
                            'input--error': selectedRegion.errors && selectedRegion.errors['kumba_sap_information.destination_site']
                          }"
                        >
                          <input
                            id="region_destination_site"
                            v-model="selectedRegion.kumba_sap_information.data.destination_site"
                            :placeholder="$t('shafts.group.input_text')"
                            type="text"
                            @input="validateInput('kumba_sap_information.destination_site')"
                          >
                        </div>
                      </div>
                    </FormRow>

                    <FormRow
                      :errors="selectedRegion.errors && selectedRegion.errors['kumba_sap_information.equipment_number']"
                      :label="$t('regions.sap.equipment_number')"
                      labelFor="region_equipment_no"
                      :modifiers="['narrow']"
                    >
                      <div class="form-row__input">
                        <div
                          :class="{
                            'input': true,
                            'input--error': selectedRegion.errors && selectedRegion.errors['kumba_sap_information.equipment_number']
                          }"
                        >
                          <input
                            id="region_equipment_no"
                            v-model="selectedRegion.kumba_sap_information.data.equipment_number"
                            :placeholder="$t('shafts.group.input_text')"
                            type="text"
                            @input="validateInput('kumba_sap_information.equipment_number')"
                          >
                        </div>
                      </div>
                    </FormRow>
                  </div>
                </template>
              </div>
            </div>

            <div class="form form--separated">
              <div class="form-row">
                <button
                  class="button button--full-width typo-ignore typo-input"
                  type="button"
                  @click="deleteCurrentRegion"
                >
                  {{ $t('regions.delete_region') }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="layout-regions__map-wrapper">
          <HeaderBar :modifiers="['light', 'border-bottom']">
            <div class="radio-button radio-button--icon-only">
              <input
                id="tool_hand"
                v-model="tool"
                aria-label="Hand tool"
                class="radio-button__input"
                type="radio"
                value="hand"
              >
              <label
                class="radio-button__content"
                for="tool_hand"
              >
                <Hand32Svg class="radio-button__icon" />
              </label>
            </div>
            <div class="radio-button">
              <input
                id="tool_new_region"
                v-model="tool"
                class="radio-button__input"
                type="radio"
                value="new_region"
              >
              <label
                class="radio-button__content"
                for="tool_new_region"
              >
                <NewRegionSvg class="radio-button__icon" />
                <span class="radio-button__label">{{ $t('regions.new_region') }}</span>
              </label>
            </div>
            <template #right>
              <UndoRedo
                v-if="!loading"
                ref="undoRedo"
                :getState="getState"
                :map="map"
                @update="setState"
              />
            </template>
          </HeaderBar>
          <div class="layout-regions__map">
            <LeafletMap
              v-if="currentOperation"
              ref="map"
              :defaultPolygonOptions="regionPolygonOptions('site', false, false)"
              :editable="true"
              :operationBounds="currentOperation.bounds"
              :overlays="currentOperation.map_tile_overlays"
              @init="onMapInit"
              @polygonClick="selectRegionOnMap"
              @polygonComplete="createPoly"
              @polygonUpdated="updatePoly"
            />
            <Loader
              v-if="loading"
              :modifiers="['overlay']"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive, nextTick, watch } from 'vue';

// Utils
import { useRouter } from 'vue-router';
import I18n from 'i18n-js';
import { v4 as uuid } from 'uuid';
import { rgeoPolygonToGeoJSON, geoJSONPolygonToLatLngs, latLngsToRgeo, latLngsToGeoJSON } from '@/functions/geospatial_conversions';
import regionPolygonOptions from '@/functions/region_polygon_options';
import useAjax from '@/functions/use_ajax';
import onEscapePressed from '@/functions/on_escape_pressed';
import { compareByAreaAndId } from '@/functions/compare_regions';
import booleanContains from '@turf/boolean-contains';
import { useOperation } from '@/functions/use_remote_object';
import { findAlteredRegion, filterAlteredRegion, clearError } from '@/functions/region';

// Components
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import Select from '@/components/form/select.vue';
import AlertBar from '@/components/form/alert_bar.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Loader from '@/components/generic/loader.vue';
import RegionTree from '@/components/regions/region_tree.vue';
import UndoRedo from '@/components/mapping/undo_redo.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';

// Svg
import Hand32Svg from '@/images/hand-32.svg';
import NewRegionSvg from '@/images/new-region.svg';
import ExclamationOctagonSvg from '@/images/exclamation_octogon.svg';

const props = defineProps({
  organisationId: { type: String, required: true },
  operationId: { type: String, required: true },
});

const router = useRouter();
const ajax = useAjax();

const currentOperation = useOperation(props);
const loading = ref(true);
const regions = ref(null);
const unsortedRegions = ref(null);
const regionMap = ref(null);
const regionTree = ref([]);
const polygonMap = new Map();
const selectedRegion = ref(null);
const tool = ref('hand');
const saving = ref(false);
const undoRedo = ref(null);
const map = ref(null);

const operationPath = computed(() => `/organisations/${props.organisationId}/operations/${props.operationId}`);
const regionsPath = computed(() => `${operationPath.value}/regions`);
const errorMessage = computed(() => null);

const regionTypeOptions = Object.entries(I18n.t('region_types'))
  .map(([value, label]) => ({
    label,
    value,
  }));

const regionTypeLabelIcons = {};
regionTypeOptions.forEach(({ value }) => {
  regionTypeLabelIcons[value] = { name: 'circle_16', class: `icon icon--small region-type region-type--${value}` };
});

onEscapePressed(event => {
  if (map.value.isDrawing()) {
    map.value.stopDrawing();
    tool.value = 'hand';
    event.preventDefault();
  }
});

watch(tool, () => {
  switch (tool.value) {
    case 'hand': {
      map.value.stopDrawing();
      break;
    }
    case 'new_region': {
      deselectRegion();
      map.value.startPoly();
      break;
    }
  }
});

function contains(region, other) {
  let p1 = polygonMap.get(region.id);
  let p2 = polygonMap.get(other.id);

  if (!p1.getBounds().contains(p2.getBounds())) return false;
  return booleanContains(region.geoJSON, other.geoJSON);
}

function updateRegionHierarchy() {
  let regions = [...regionMap.value.values()].filter(region => !region._destroy);
  regions.forEach(region => {
    region.parent = null;
    region.children = [];
  });
  regions.sort(compareByAreaAndId);
  regions
    .forEach(region => {
      let x = regions
        .filter(other => {
          return other.id !== region.id &&
            contains(region, other) &&
            !contains(other, region); // check that they don't contain each other (same poly)
        });
      x.forEach(other => {
          other.parent = region;
          if (polygonMap.get(other.id)) {
            polygonMap.get(other.id).bringToFront();
          }
        });
    });
  regions.forEach(region => {
    if (region.parent) region.parent.children.push(region);
  });

  regionTree.value = regions.filter(region => !region.parent);
}

async function onMapInit() {
  regionMap.value = new Map();

  await loadRegions();

  regions.value.forEach(loadRegion);

  if (regions.value.length > 0) {
    selectRegion(regions.value[0]);
  }

  updateRegionHierarchy();
  loading.value = false;
}

async function loadRegions() {
  let { ok, data } = await ajax(regionsPath.value);

  if (ok) {
    regions.value = data.regions;
    unsortedRegions.value = data.unsorted_regions;
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
  };
  loadPolygon(regionObject);

  regionMap.value.set(region.id, reactive(regionObject));
}

function loadPolygon(region) {
  let polygon = map.value.addPolygon(region.latLngs, regionPolygonOptions(region.region_type));
  polygon.disableEdit();
  polygonMap.set(region.id, polygon);
  polygon.region = region;
}

function deselectRegion() {
  if (selectedRegion.value) {
    let previousRegion = regionMap.value.get(selectedRegion.value.id);
    previousRegion.selected = false;
    selectedRegion.value = null;

    let previousPolygon = polygonMap.get(previousRegion.id);
    previousPolygon.disableEdit();
    updatePolygonStyle(previousRegion, previousPolygon);
  }
}

async function selectRegion(region, { goTo = true } = {}) {
  if (!region) return;

  deselectRegion();

  let newRegion = regionMap.value.get(region.id);
  let newPolygon = polygonMap.get(region.id);
  updatePolygonStyle(newRegion, newPolygon, true);
  newPolygon.enableEdit();
  selectedRegion.value = newRegion;

  /**
   * Add SAP data
   * If SAP is enabled for an operation that already has regions without SAP data
  */
  if (currentOperation.value?.policy?.sap_v1 && !selectedRegion.value?.kumba_sap_information) {
    selectedRegion.value['kumba_sap_information'] = {
      lookup: 'queued',
      data: {
        functional_location: null,
        equipment_number: null,
        work_centre: null,
        planner_group: null,
        training_asset: null,
        destination_site: null
      }
    };
  }

  if (goTo) map.value.goToObject(newPolygon);

  await nextTick();
  newRegion.selected = true;
}

async function selectRegionOnMap(polygon) {
  await selectRegion(polygon.region);
}

function updatePoly(polygon, latLngs) {
  let region = polygon.region;
  region.bounds = latLngsToRgeo(latLngs);
  region.geoJSON = latLngsToGeoJSON(latLngs);
  region.latLngs = latLngs;
  setTimeout(updateRegionHierarchy, 1);
}

function createPoly(polygon, latLngs) {
  let region = {
    id: uuid(),
    region_type: 'site',
    name: '',
    isNew: true,
    latLngs: latLngs,
    bounds: latLngsToRgeo(latLngs),
    geoJSON: latLngsToGeoJSON(latLngs),
    selected: true,
  };

  // Add for SAP
  if (currentOperation.value?.policy?.sap_v1) {
    region['kumba_sap_information'] = {
      lookup: 'queued',
      data: {
        functional_location: null,
        equipment_number: null,
        work_centre: null,
        planner_group: null,
        training_asset: null,
        destination_site: null
      }
    };
  }
  /**
   * asset_name
   *
   */
  polygon.region = region;

  regionMap.value.set(region.id, region);
  unsortedRegions.value.push(region);
  polygonMap.set(region.id, polygon);

  selectedRegion.value = region;

  updateRegionHierarchy();
  tool.value = 'hand';

  undoRedo.value.pushUndo();
}

function deleteRegion(regionToDelete, pushUndo) {
  if (regionToDelete === selectedRegion.value) deselectRegion();
  let polygon = polygonMap.get(regionToDelete.id);
  if (polygon) {
    polygon.remove();
    polygonMap.delete(regionToDelete.id);
  }
  if (regionToDelete.isNew) {
    regionMap.value.delete(regionToDelete.id);
    const objWithIdIndex = unsortedRegions.value.findIndex((obj) => obj.id === regionToDelete.id);
    unsortedRegions.value.splice(objWithIdIndex, 1);
  } else {
    regionToDelete._destroy = true;
  }
  updateRegionHierarchy();
  if (pushUndo) undoRedo.value.pushUndo();
}

function deleteCurrentRegion() {
  deleteRegion(selectedRegion.value, true);
}

function formatRegionForPost(region) {
  let regionJson = {
    name: region.name,
    external_reference: region.external_reference,
    bounds: region.bounds,
    region_type: region.region_type,
  };

  // Modify for SAP
  if (currentOperation.value?.policy?.sap_v1 && region?.kumba_sap_information) {
    regionJson['kumba_sap_information_attributes'] = region.kumba_sap_information;

    // Sets queued to only altered regions
    if (region?.altered) regionJson['kumba_sap_information_attributes']['lookup'] = 'queued';
  }

  if (!region.isNew) {
    if (region._destroy) {
      return { id: region.id, _destroy: true };
    }
    return {
      ...regionJson,
      id: region.id,
    };
  }

  return regionJson;
}

async function save() {
  saving.value = true;
  const fieldList = {
    main: ['bounds', 'external_reference', 'name', 'region_type'],
    kumba: ['destination_site', 'equipment_number', 'functional_location', 'planner_group', 'work_centre']
  };

  // Finds the altered region/regions and marks them with the altered key
  let alteredRegions = findAlteredRegion([...regionMap.value.values()], unsortedRegions.value, fieldList);

  // Clear region errors
  alteredRegions = clearError(alteredRegions);

  // Reset the region style
  selectedRegion.value && updatePolygonStyle(selectedRegion.value);
  updateRegionHierarchy();

  // Get list of altered, deleted and new regions
  alteredRegions = filterAlteredRegion(alteredRegions);

  if (alteredRegions.length <= 0) return router.push({ name: 'configurationPlants' });

  let { ok, data } = await ajax(regionsPath.value, { method: 'PATCH', body: {
    operation: {
      regions_attributes: alteredRegions.map(formatRegionForPost),
    }
  }});

  if (ok) {
    router.push({ name: 'configurationPlants' });
  } else {
    postPatchError(data);
  }

  saving.value = false;
}

function postPatchError(data) {
  [...unsortedRegions.value.values()].filter((r) => !r._destroy).forEach((region, index) => {
    const errorObject = data.regions[index]?.region;
    region.errors = errorObject;
    // If the selected region has no errors we select the one that has errors
    if (!selectedRegion.value?.errors && errorObject) {
      selectRegion(region);
      selectedRegion.value['errors'] = region.errors;
    }
    updatePolygonStyle(region);
  });

  updateRegionHierarchy();
}

function validateInput(field) {
  if (selectedRegion.value?.errors) {
    selectedRegion.value.errors[field] = null;
  }

  const isValid = selectedRegion.value?.errors && !Object.keys(selectedRegion.value?.errors).find(key => selectedRegion.value?.errors[key] !== null);
  if (isValid) {
    return [...unsortedRegions.value.values()].filter((r) => !r._destroy)?.forEach((region) => {
      // Remove validation for the selected region
      region.selected && (region.errors = null);
      updatePolygonStyle(region);
    });
  }
}

function updatePolygonStyle(region, polygon = polygonMap.get(region.id), selected = region.selected) {
  let style = regionPolygonOptions(region.region_type, selected, region.errors);
  polygon?.setStyle(style);
}

function updateRegionType() {
  let region = regionMap.value.get(selectedRegion.value.id);
  updatePolygonStyle(region);
}

function getState() {
  let state = {};
  for (let region of regionMap.value.values()) {
    if (region._destroy) continue;
    state[region.id] = {
      id: region.id,
      name: region.name,
      region_type: region.region_type,
      bounds: region.bounds,
      isNew: region?.isNew,
    };
  }
  return state;
}

function setState(state) {
  for (let [id, value] of Object.entries(state)) {
    let region = regionMap.value.get(id);
    if (region) {
      updateRegionFromState(region, value);
    } else {
      loadRegion(value);
      unsortedRegions.value.push(value);
    }
  }
  for (let region of regionMap.value.values()) {
    if (!state[region.id]) {
      deleteRegion(region, false);
    }
  }

  updateRegionHierarchy();
}

function updateRegionFromState(region, state) {
  region.name = state.name;
  region.region_type = state.region_type;
  region.bounds = state.bounds;
  region.geoJSON = rgeoPolygonToGeoJSON(state.bounds);
  region.latLngs = geoJSONPolygonToLatLngs(region.geoJSON);
  if (region._destroy) region._destroy = false;
  let polygon = polygonMap.get(region.id);
  if (!polygon) {
    loadPolygon(region);
  } else {
    polygon.setLatLngs(region.latLngs);
    polygon.editor?.reset();
  }
}
</script>
